<template>
  <div class="relative h-full w-full overflow-hidden">
    <!-- In a hero the poster is the LCP element, and `<video poster>` accepts no fetchpriority of
         its own, so a preload is the only way out of the default-priority queue. `rel=preload` is
         body-ok, which keeps this a plain element rather than a Nuxt head dependency. -->
    <link v-if="critical && poster" rel="preload" as="image" :href="poster" fetchpriority="high" />

    <!-- Delivered with `ac_none` and unmutable, so there is no audio for a caption track. -->
    <!-- eslint-disable-next-line vuejs-accessibility/media-has-caption -->
    <!-- Keyed on the url: patching the `<source>`s of a `<video>` that already picked one does
         nothing, and the previous film would play on under the new poster. -->
    <video
      v-if="showVideo"
      :key="url"
      ref="videoEl"
      class="h-full w-full object-cover"
      :class="videoClass"
      :poster="poster"
      :aria-label="alt || undefined"
      loop
      muted
      playsinline
      preload="none"
      @error="onVideoError"
      @play="isPlaying = true"
      @pause="isPlaying = false"
    >
      <!-- Withheld until the first play: WebKit fetches every `<source>` despite `preload="none"`. -->
      <template v-if="sourcesArmed">
        <source
          v-for="(source, index) in sources"
          :key="source.src"
          :src="source.src"
          :type="source.type"
          :media="source.media"
          @error="onSourceError(index)"
        />
      </template>
    </video>

    <!-- Reduced motion, or a clip the browser could not play: the first frame, held still. -->
    <img
      v-else-if="poster"
      class="h-full w-full object-cover"
      :class="videoClass"
      :src="poster"
      :alt="alt"
      :fetchpriority="critical ? 'high' : 'auto'"
      :loading="critical ? 'eager' : 'lazy'"
      decoding="async"
    />

    <!-- WCAG 2.2.2: anything that moves for more than five seconds needs a way to stop it. -->
    <button
      v-if="showVideo && isMounted"
      type="button"
      :class="controlClass"
      :aria-label="isPlaying ? $t('video.pause') : $t('video.play')"
      @click="toggle"
    >
      <svg
        v-if="isPlaying"
        class="h-4 w-4"
        viewBox="0 0 16 16"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M4 2.5h3v11H4v-11Zm5 0h3v11H9v-11Z" />
      </svg>
      <svg v-else class="h-4 w-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M4 2.5v11l9-5.5-9-5.5Z" />
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  DEFAULT_VIDEO_WIDTH,
  cloudinaryVideoPoster,
  cloudinaryVideoSources,
} from '../../libs/cloudinaryVideo'

/**
 * A Cloudinary clip standing in for a still: it fills its slot the way an `<Image>` would and
 * plays as a silent, looping background film.
 *
 * `cloudinaryVideoSources` keeps the bytes honest. On top of that the element carries
 * `preload="none"` and deliberately no `autoplay`, which would override that hint and fetch on page
 * load; an IntersectionObserver starts playback instead, so a clip further down the page costs
 * nothing until it is reached. The hint alone is not enough for WebKit, so the `<source>`s are only
 * rendered at that point too. Reduced motion gets the poster, and so does anyone without JS.
 */
const props = withDefaults(
  defineProps<{
    /** Cloudinary video URL, as stored by the Contentful Cloudinary field. */
    url?: string
    /** Accessible name. Leave empty when the clip is decorative next to the copy it illustrates. */
    alt?: string
    /** Extra classes on the media element itself (the wrapper always fills its parent). */
    videoClass?: string | string[]
    /** Delivery width cap. Raise it only for a slot wider than a landing block's media. */
    width?: number
    /**
     * Above the fold, where the poster is the LCP element: prioritises it and holds the clip
     * itself back until the page has loaded. Wrong anywhere else — it front-loads a megabyte.
     */
    critical?: boolean
  }>(),
  { url: '', alt: '', videoClass: undefined, width: DEFAULT_VIDEO_WIDTH, critical: false },
)

// Kept out of the template so the markup stays inside the 100-column width Prettier is set to.
const controlClass = [
'absolute bottom-6 xl:bottom-3 right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full',
  'bg-blue-union/50 text-white transition hover:bg-blue-union',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
  'focus-visible:outline-white',
].join(' ')

const sources = computed(() => cloudinaryVideoSources(props.url, props.width))
const poster = computed(() => cloudinaryVideoPoster(props.url, props.width))

// Unreadable during SSR, so render the film and swap on mount. Same shape as BigMediaCta.
const motionOk = ref(true)
// Apart from `motionOk`: both end in the still, but only this one is reconsidered on a new clip.
const videoFailed = ref(false)
const showVideo = computed(() => motionOk.value && !videoFailed.value)
// Intent, not state — the observer also pauses off screen, which is no instruction to stay stopped.
const userPaused = ref(false)
// The two gates on fetching: in view, and past the point where the clip would compete with the
// paint. Kept apart from `userPaused` so neither can be mistaken for an instruction to stay stopped.
const isVisible = ref(false)
const canFetch = ref(false)
// Real element state, so the control never offers to pause what a rejected play() already stopped.
const isPlaying = ref(false)
// The control is inert without JS, so it is not worth putting in the SSR markup.
const isMounted = ref(false)
const videoEl = ref<HTMLVideoElement | null>(null)
let observer: IntersectionObserver | null = null

// Whether the `<source>`s are in the DOM. One-way per element: scrolling off pauses the film, and
// pulling its sources then would throw away what it already buffered.
const sourcesArmed = ref(false)

const play = () => {
  // Rejected under some autoplay policies; the poster stays up and `pause` keeps the control honest.
  void videoEl.value?.play().catch(() => {})
}

// Waits a tick when it has just armed, so the element has sources to select from when asked.
const armAndPlay = () => {
  if (sourcesArmed.value) return play()
  sourcesArmed.value = true
  void nextTick(play)
}

// Nothing is fetched until all three agree — the sources are not even rendered before then.
const maybePlay = () => {
  if (canFetch.value && isVisible.value && !userPaused.value) armAndPlay()
}

// Named so unmount can take it back off `window`: a step change before `load` would otherwise
// leave this closure pinned to the window until the page finishes arriving.
const onPageLoad = () => {
  canFetch.value = true
  maybePlay()
}

const toggle = () => {
  const element = videoEl.value
  if (!element) return
  userPaused.value = !element.paused
  if (userPaused.value) element.pause()
  // Asking for it outranks the wait below: someone looking at the control wants the film now.
  else {
    canFetch.value = true
    armAndPlay()
  }
}

// A `<source>` erroring is routine: Firefox rejects the HEVC entry on sight and walks on to VP9.
// Only the last candidate failing means there is nothing left to try.
//
// Position is the one reliable signal here. `networkState` cannot tell the two apart — it is set to
// NETWORK_NO_SOURCE when selection begins and only moves once a resource is actually fetched, which
// a source rejected on its `type` never is. Reading it here gave up on the first rejection and left
// every non-Safari browser looking at the poster.
const onSourceError = (index: number) => {
  if (index === sources.value.length - 1) videoFailed.value = true
}

// The element itself only errors once it has chosen a resource and then failed to decode it, so
// there is no walking left to do by then.
const onVideoError = () => {
  videoFailed.value = true
}

// A new clip deserves a fresh attempt; otherwise one broken file pins the component to the still
// for good. The `:key` recreates the element, which the observer below picks up.
watch(
  () => props.url,
  () => {
    videoFailed.value = false
    userPaused.value = false
    isPlaying.value = false
    // The recreated element is unarmed until it, too, is in view.
    sourcesArmed.value = false
  },
)

onMounted(() => {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    motionOk.value = false
    return
  }

  isMounted.value = true

  // No clip is worth a byte of the initial load — decorative in the hero, decorative further down.
  // The observer alone was not deferral enough: its 200px margin catches the second landing block
  // on a phone, so a below-the-fold film was pulling half a megabyte while the page was still
  // arriving. Waiting for `load` costs only the poster staying up a little longer.
  if (document.readyState !== 'complete') {
    window.addEventListener('load', onPageLoad, { once: true })
  } else {
    canFetch.value = true
  }

  // Without an observer to say when it is in view, nothing else would ever open the visibility gate.
  if (typeof IntersectionObserver === 'undefined') {
    isVisible.value = true
    maybePlay()
    return
  }

  // The only thing that fetches the clip at all, and it stops the decode again once out of view.
  // Reads the element off the entry rather than a captured ref, since a swapped url replaces it.
  observer = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        isVisible.value = entry.isIntersecting
        if (!entry.isIntersecting) (entry.target as HTMLVideoElement).pause()
        else maybePlay()
      }
    },
    { rootMargin: '200px', threshold: 0.01 },
  )

  // Re-observes when the element is recreated. Observing fires the callback at once, so a clip
  // already in view plays.
  watch(
    videoEl,
    element => {
      observer?.disconnect()
      if (element) observer?.observe(element)
    },
    { immediate: true },
  )
})

onBeforeUnmount(() => {
  observer?.disconnect()
  // A no-op once `load` has fired, since `once` already removed it.
  window.removeEventListener('load', onPageLoad)
})
</script>
